<template>
    <validation-observer v-slot="{ handleSubmit }" slim>
        <form class="email-step" @submit.prevent="handleSubmit(submit)">
            <p class="email-step-intro">
                {{ providerLabel }} did not share an email address with us. Tell us where to reach you and we'll finish
                your account.
            </p>

            <validation-provider v-slot="{ errors }" rules="required|email" name="Email">
                <c-input
                    v-model="email"
                    type="email"
                    label="Email"
                    placeholder="Type your email"
                    :error="!!errors[0]"
                    :error-msg="errors[0]"
                />
            </validation-provider>

            <c-error v-if="errorMessage" :message="errorMessage" />

            <div class="form-actions">
                <c-button variant="outline-secondary" text="Back" :disabled="submitting" @click="emit('back')" />
                <c-button
                    type="submit"
                    variant="primary"
                    :text="submitting ? 'Creating your account...' : 'Continue'"
                    :disabled="submitting"
                />
            </div>
        </form>
    </validation-observer>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import CButton from '@/components/shared/CButton/CButton.vue';
import CError from '@/components/shared/CError.vue';
import CInput from '@/components/shared/CInput/CInput.vue';
import { completeExternalSignUp } from '@/api/auth';
import { externalAuthErrorMessage } from '@/utils/authUtils';
import type { ExternalAuthResult } from '@/typings/auth/externalAuth';

const props = defineProps<{ pendingTicket: string; providerLabel: string }>();
const emit = defineEmits<{
    (event: 'back'): void;
    (event: 'completed', result: ExternalAuthResult): void;
}>();

const email = ref('');
const submitting = ref(false);
const errorMessage = ref('');

const submit = async () => {
    errorMessage.value = '';

    submitting.value = true;
    try {
        const result = await completeExternalSignUp({
            pendingTicket: props.pendingTicket,
            email: email.value.trim()
        });
        // Stays disabled: the page is leaving for Stripe and the ticket is spent
        emit('completed', result);
    } catch (error) {
        errorMessage.value = externalAuthErrorMessage(error, 'We could not create your account. Please try again.');
        submitting.value = false;
    }
};
</script>

<style scoped>
.email-step {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.form-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
}

.email-step-intro {
    margin: 0;
    font-size: 14px;
    color: #333;
}
</style>
